import React, { useState } from 'react'
import { theme } from '../../../core/design-system/theme'
import { TrustBadge } from '../../../components/shared/TrustBadge'
import { useAboutViewModel } from '../hooks/useAboutViewModel'

const TRUST_BADGES = [
  'Background checked',
  'ID verified',
  'First aid certified',
  '5-star rated',
]

const prefersReducedMotion =
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

function ChevronIcon({ open }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      stroke={theme.colors.textSecondary}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
        transition: prefersReducedMotion ? 'none' : 'transform 0.2s ease',
        flexShrink: 0,
      }}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  )
}

function FAQItem({ entry, isOpen, onToggle }) {
  const [hovered, setHovered] = useState(false)
  return (
    <div
      style={{
        background: theme.colors.card,
        border: '1px solid ' + (hovered ? theme.colors.borderStrong : theme.colors.border),
        borderRadius: 14,
        marginBottom: 8,
        overflow: 'hidden',
        transition: 'border-color 0.15s ease',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        onClick={onToggle}
        aria-expanded={isOpen}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          padding: '14px 16px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span
          style={{
            fontFamily: theme.fonts.body,
            fontWeight: 600,
            fontSize: 14,
            color: theme.colors.textPrimary,
          }}
        >
          {entry.question}
        </span>
        <ChevronIcon open={isOpen} />
      </button>

      <div
        style={{
          maxHeight: isOpen ? 400 : 0,
          overflow: 'hidden',
          transition: prefersReducedMotion ? 'none' : 'max-height 0.25s ease',
        }}
      >
        <p
          style={{
            fontFamily: theme.fonts.body,
            fontSize: 14,
            color: theme.colors.textSecondary,
            lineHeight: 1.6,
            margin: 0,
            padding: '0 16px 14px',
          }}
        >
          {entry.answer}
        </p>
      </div>
    </div>
  )
}

function OurStorySection() {
  const paragraphs = [
    'One night, we went out to dinner with our baby — like so many parents do, hoping to enjoy a meal but expecting the usual juggling act.',
    'Then something unexpected happened.',
    'A friend\'s mom, visiting from Italy, gently took our baby and held them while we ate. No rush. No stress. Just a moment to sit, talk, and feel like ourselves again.',
    'And in that moment, it clicked: This is what people mean when they say "it takes a village." But so many of us don\'t have that village nearby. So we built this — to bring that feeling back. A way for parents to go out, connect, and breathe — knowing someone is there to help, even for just a little while.',
  ]

  return (
    <div
      id="our-story"
      style={{
        background: theme.colors.bgSecondary,
        borderRadius: 16,
        border: `1px solid ${theme.colors.border}`,
        padding: 20,
        marginBottom: 28,
      }}
    >
      <h2
        style={{
          fontFamily: theme.fonts.display,
          fontSize: 20,
          fontWeight: 600,
          color: theme.colors.textPrimary,
          margin: '0 0 16px',
        }}
      >
        Our Story
      </h2>
      {paragraphs.map((text, i) => (
        <p
          key={i}
          style={{
            fontFamily: theme.fonts.body,
            fontSize: 14,
            color: theme.colors.textSecondary,
            lineHeight: 1.7,
            margin: i < paragraphs.length - 1 ? '0 0 12px' : 0,
          }}
        >
          {text}
        </p>
      ))}
    </div>
  )
}

export default function AboutView({ repository }) {
  const { status, entries, error } = useAboutViewModel({ repository })
  const [openId, setOpenId] = useState(null)

  function toggle(id) {
    setOpenId((prev) => (prev === id ? null : id))
  }

  return (
    <main
      className="page-enter"
      style={{
        background: theme.colors.cream,
        minHeight: '100vh',
        paddingBottom: 90,
      }}
    >
      {/* Hero strip */}
      <div
        style={{
          background: `linear-gradient(135deg, ${theme.colors.terracottaDark} 0%, ${theme.colors.terracotta} 60%, ${theme.colors.terracottaLight} 100%)`,
          padding: '40px 24px 36px',
        }}
      >
        <h1
          style={{
            fontFamily: theme.fonts.display,
            fontWeight: 600,
            fontSize: 24,
            color: '#FFFFFF',
            margin: '0 0 10px',
            lineHeight: 1.3,
          }}
        >
          Trusted help for a smoother family outing
        </h1>
        <p
          style={{
            fontFamily: theme.fonts.body,
            fontSize: 14,
            color: 'rgba(255,255,255,0.85)',
            margin: 0,
          }}
        >
          Book a vetted grandmother — as easily as Uber.
        </p>
      </div>

      <div style={{ maxWidth: 480, margin: '0 auto', padding: '24px 20px 0' }}>
        <OurStorySection />

        {/* Trust badges row */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            marginBottom: 32,
          }}
        >
          {TRUST_BADGES.map((label) => (
            <TrustBadge key={label} label={label} withIcon />
          ))}
        </div>

        {/* FAQ */}
        <h2
          style={{
            fontFamily: theme.fonts.display,
            fontWeight: 600,
            fontSize: 18,
            color: theme.colors.textPrimary,
            margin: '0 0 16px',
          }}
        >
          Frequently asked questions
        </h2>

        {status === 'loading' && (
          <div aria-label="Loading FAQ" aria-busy="true">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                style={{
                  height: 52,
                  background: theme.colors.bgSecondary,
                  borderRadius: 14,
                  marginBottom: 8,
                  opacity: 0.7,
                }}
              />
            ))}
          </div>
        )}

        {status === 'failed' && (
          <p
            role="alert"
            style={{
              fontFamily: theme.fonts.body,
              color: theme.colors.errorText,
              padding: 16,
              background: theme.colors.errorBg,
              borderRadius: 12,
              margin: 0,
            }}
          >
            {error}
          </p>
        )}

        {status === 'loaded' && entries.length === 0 && (
          <p
            style={{
              fontFamily: theme.fonts.body,
              fontSize: 14,
              color: theme.colors.textSecondary,
              textAlign: 'center',
              margin: 0,
            }}
          >
            No FAQ entries available.
          </p>
        )}

        {status === 'loaded' &&
          entries.map((entry) => (
            <FAQItem
              key={entry.id}
              entry={entry}
              isOpen={openId === entry.id}
              onToggle={() => toggle(entry.id)}
            />
          ))}
      </div>
    </main>
  )
}
